import { AbstractControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Component, Injector, Input, TemplateRef, ChangeDetectionStrategy } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { DatePickerModule } from '@syncfusion/ej2-angular-calendars';
import { DropDownListModule } from '@syncfusion/ej2-angular-dropdowns';
import { AppErrorMessagesComponent } from '../../error-messages/app-error-messages.component';
import { YesNoGroupButtonComponent } from '../yes-no-group-button/yes-no-group-button.component';
import { DynamicFormFieldDefinition, DynamicFormLayoutRow, DynamicReferenceConfig } from './dynamic-entity-definition';
import { NgTemplateOutlet } from '@angular/common';
import { EnumDropDownListComponent } from '../enum-drop-down-list/enum-drop-down-list.component';
import { DynamicAutocompleteComponent } from '../dynamic-autocomplete/dynamic-autocomplete.component';
import { IDynamicAutocompleteItem } from '../dynamic-autocomplete/dynamic-autocomplete-item';
import { IAdaptorService } from '../syncfusion/adaptor-service.interface';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { FilterDTO, FilterOperator, SortDirection } from '../../models/models.swagger.generated';

@Component({
  selector: 'app-dynamic-edit-form',
  templateUrl: './dynamic-edit-form.component.html',
  styleUrls: ['./dynamic-edit-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    ReactiveFormsModule,
    AppErrorMessagesComponent,
    YesNoGroupButtonComponent,
    DatePickerModule,
    DropDownListModule,
    TranslatePipe,
    NgTemplateOutlet,
    EnumDropDownListComponent,
    DynamicAutocompleteComponent,
  ],
})
export class DynamicEditFormComponent {
  @Input({required: true}) public formGroup!: FormGroup | null;
  @Input() public fields: DynamicFormFieldDefinition<any>[] = [];
  @Input() public layout: DynamicFormLayoutRow<any>[] = [];
  @Input() public isEditMode = false;
  @Input() public customFieldTemplates: { [fieldKey: string]: TemplateRef<any> } = {};
  @Input() public initialReferenceItems: { [fieldKey: string]: IDynamicAutocompleteItem } = {};

  constructor(
    private translateService: TranslateService,
    private injector: Injector,
  ) {
  }

  private _referenceDataFunctions = new Map<string, (searchText: string) => Observable<IDynamicAutocompleteItem[]>>();

  public getReferenceDataFunction(field: DynamicFormFieldDefinition<any>): (searchText: string) => Observable<IDynamicAutocompleteItem[]> {
    if (this._referenceDataFunctions.has(field.key)) {
      return this._referenceDataFunctions.get(field.key)!;
    }

    const config = field.referenceConfig;
    if (!config) {
      const fn = () => of([]);
      this._referenceDataFunctions.set(field.key, fn);
      return fn;
    }

    const service = this.injector.get<IAdaptorService>(config.serviceType);
    const fn = (searchText: string): Observable<IDynamicAutocompleteItem[]> => {
      const filters: FilterDTO[] = searchText
        ? [new FilterDTO({ isComplex: false, field: config.searchField, operator: FilterOperator.Contains, value: searchText })]
        : [];
      return service.getData(0, 10, filters, config.searchField, SortDirection.Ascending).pipe(
        map(result => (result?.items ?? []).map(item => ({
          id: item[config.valueField],
          name: item[config.textField],
        })))
      );
    };
    this._referenceDataFunctions.set(field.key, fn);
    return fn;
  }

  public get visibleRows(): DynamicFormFieldDefinition<any>[][] {
    const visibleFieldKeys = new Set(this.getVisibleFields().map((field) => field.key));
    const byKey = new Map(this.fields.map((field) => [field.key, field]));

    const layoutRows = this.layout
      .map((row) => row.fields
        .map((fieldKey) => byKey.get(fieldKey))
        .filter((field): field is DynamicFormFieldDefinition<any> => !!field && visibleFieldKeys.has(field.key)))
      .filter((row) => row.length > 0);

    const missingFields = this.getVisibleFields().filter((field) => !this.layout.some((row) => row.fields.includes(field.key)));
    if (missingFields.length > 0) {
      layoutRows.push(missingFields);
    }

    return layoutRows;
  }

  public getColClass(field: DynamicFormFieldDefinition<any>): string {
    return `form-group col-md-${field.colMd ?? 12}`;
  }

  public isTextInput(field: DynamicFormFieldDefinition<any>): boolean {
    return field.control === 'text' || field.control === 'email' || field.control === 'number';
  }

  public isReadonly(field: DynamicFormFieldDefinition<any>): boolean {
    if (this.isEditMode) {
      return !!field.readOnlyOnEdit;
    }
    return !!field.readOnlyOnAdd;
  }

  public getInputType(field: DynamicFormFieldDefinition<any>): string {
    if (field.control === 'email') {
      return 'email';
    }
    if (field.control === 'number') {
      return 'number';
    }
    return 'text';
  }

  public getDropDownDataSource(field: DynamicFormFieldDefinition<any>): any[] {
    var options = field.options ?? [];
    options.forEach(option => {
      if (!option.text && option.textKey) {
        option.text = this.translateService.instant(option.textKey);
      }
    });
    return options;
  }

  public isRequired(field: DynamicFormFieldDefinition<any>): boolean {
    return this.formGroup?.get(field.key)?.hasValidator(Validators.required) ?? false;
  }

  public getControl(fieldKey: string): AbstractControl | null {
    return this.formGroup?.get(fieldKey) ?? null;
  }

  private getVisibleFields(): DynamicFormFieldDefinition<any>[] {
    return this.fields.filter((field) => {
      if (this.isEditMode && field.hiddenOnEdit) {
        return false;
      }
      if (!this.isEditMode && field.hiddenOnAdd) {
        return false;
      }
      return true;
    });
  }
}
